import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { fmt, fmtShort } from "@/lib/mis-utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AmountCell({ amount, colorize = true, className = "" }) {
  const isNeg = amount < 0;
  const color = !colorize ? "text-slate-900" : isNeg ? "text-red-600" : "text-emerald-700";
  return (
    <span className={`font-mono tabular-nums text-sm ${color} ${className}`}>
      {isNeg ? "-" : ""}{fmt(amount)}
    </span>
  );
}

export function MISComparison({ companyContext }) {
  const [p1From, setP1From] = useState("");
  const [p1To, setP1To] = useState("");
  const [p2From, setP2From] = useState("");
  const [p2To, setP2To] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCompare = async () => {
    if (!p1From || !p1To || !p2From || !p2To) return;
    setLoading(true);
    try {
      const params = { p1_from: p1From, p1_to: p1To, p2_from: p2From, p2_to: p2To };
      if (companyContext?.companyId) params.company_id = companyContext.companyId;
      if (companyContext?.branchId) params.branch_id = companyContext.branchId;
      const res = await axios.get(`${API}/mis/comparison`, { params });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="mis-comparison-tab">
      <Card className="border border-slate-200 shadow-none">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Period 1</label>
              <div className="flex items-center gap-2 mt-1">
                <input type="date" value={p1From} onChange={(e) => setP1From(e.target.value)}
                  className="px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                  data-testid="comp-p1-from" />
                <span className="text-xs text-slate-400">to</span>
                <input type="date" value={p1To} onChange={(e) => setP1To(e.target.value)}
                  className="px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                  data-testid="comp-p1-to" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Period 2</label>
              <div className="flex items-center gap-2 mt-1">
                <input type="date" value={p2From} onChange={(e) => setP2From(e.target.value)}
                  className="px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                  data-testid="comp-p2-from" />
                <span className="text-xs text-slate-400">to</span>
                <input type="date" value={p2To} onChange={(e) => setP2To(e.target.value)}
                  className="px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                  data-testid="comp-p2-to" />
              </div>
            </div>
          </div>
          <Button size="sm" className="mt-3" onClick={handleCompare}
            disabled={loading || !p1From || !p1To || !p2From || !p2To} data-testid="btn-compare">
            {loading ? "Comparing..." : "Compare"}
          </Button>
        </CardContent>
      </Card>

      {data && (
        <>
          <Card className="border border-slate-200 shadow-none">
            <CardHeader className="pb-2 bg-slate-50">
              <CardTitle className="text-sm font-semibold text-slate-700">P&L Summary Comparison</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold min-w-[180px]">Particulars</TableHead>
                    <TableHead className="text-right text-xs font-semibold w-32">Period 1</TableHead>
                    <TableHead className="text-right text-xs font-semibold w-32">Period 2</TableHead>
                    <TableHead className="text-right text-xs font-semibold w-32">Variance</TableHead>
                    <TableHead className="text-right text-xs font-semibold w-20">%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.summary.map((row) => (
                    <TableRow key={row.name} className={row.significant ? "bg-amber-50/50" : ""}>
                      <TableCell className={`text-sm ${["Gross Profit", "EBITDA", "PBT"].includes(row.name) ? "font-bold" : ""}`}>
                        {row.name}
                      </TableCell>
                      <TableCell className="text-right"><AmountCell amount={row.period1} /></TableCell>
                      <TableCell className="text-right"><AmountCell amount={row.period2} /></TableCell>
                      <TableCell className="text-right"><AmountCell amount={row.variance} /></TableCell>
                      <TableCell className={`text-right text-xs font-mono ${row.variance_pct > 0 ? "text-emerald-600" : row.variance_pct < 0 ? "text-red-600" : ""}`}>
                        {row.variance_pct > 0 ? "+" : ""}{row.variance_pct}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {Object.entries(data.sections || {}).map(([sectionName, items]) => (
            items.length > 0 && (
              <Card key={sectionName} className="border border-slate-200 shadow-none">
                <CardHeader className="pb-2 bg-slate-50/50">
                  <CardTitle className="text-xs font-semibold text-slate-600">{sectionName} Detail</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/30">
                        <TableHead className="text-xs font-semibold">Subhead</TableHead>
                        <TableHead className="text-right text-xs font-semibold w-28">Period 1</TableHead>
                        <TableHead className="text-right text-xs font-semibold w-28">Period 2</TableHead>
                        <TableHead className="text-right text-xs font-semibold w-28">Variance</TableHead>
                        <TableHead className="text-right text-xs font-semibold w-16">%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((row) => (
                        <TableRow key={row.name} className={row.significant ? "bg-amber-50/30" : ""}>
                          <TableCell className="text-sm">{row.name}</TableCell>
                          <TableCell className="text-right"><AmountCell amount={row.period1} colorize={false} /></TableCell>
                          <TableCell className="text-right"><AmountCell amount={row.period2} colorize={false} /></TableCell>
                          <TableCell className="text-right"><AmountCell amount={row.variance} /></TableCell>
                          <TableCell className={`text-right text-xs font-mono ${row.variance_pct > 0 ? "text-emerald-600" : row.variance_pct < 0 ? "text-red-600" : ""}`}>
                            {row.variance_pct > 0 ? "+" : ""}{row.variance_pct}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )
          ))}
        </>
      )}
    </div>
  );
}
